/**
 * The Formula block's LaTeX, read into a tree.
 *
 * A formula used to be a *string*: the block stored whatever the creator typed
 * and printed it back character for character. That works for `a² + b² = c²` and
 * fails at the first thing a real formula does — a fraction, which is not a
 * character but a *shape*. `\frac{x+1}{2x-3}` is two boxes, stacked, with a bar
 * between them, and there is no arrangement of characters that draws it. Which is
 * why the old keyboard inserted `(‸)⁄(‸)`: a flat string has nowhere to put a
 * second fraction, so a nested one was not expressible at all.
 *
 * This module turns the source into structure — the two boxes, and the bar — so
 * that something else can measure and draw it (see `layout.ts`, `notation.tsx`).
 * It is a *subset* of LaTeX, and an honest one: what it knows is
 * `commands.ts`, and what it does not know is kept as written
 * (`{kind: 'literal'}`) and reported in `problems`, so a formula that uses
 * `\begin{matrix}` shows the reader the source rather than an empty gap, and the
 * studio has something to tell the creator.
 *
 * Three rules from TeX decide most of the shape of this code:
 *
 *  - **Whitespace is nothing.** In maths, spaces exist to separate the creator's
 *    *tokens*, not to be printed; `\text{...}` is the exception, where they are
 *    words again.
 *  - **A script attaches to what precedes it.** `x^2` is not a list of three
 *    atoms, it is a base and its exponent, and `x_i^2` is one base with both.
 *  - **Braces group, they do not print.** `{ab}^2` is one box squared.
 */

import {
  CHAR_SPACING,
  COMMAND_SPACING,
  OP_NAME_COMMANDS,
  SPACE_COMMANDS,
  SYMBOL_COMMANDS,
  TEXT_COMMANDS,
} from './commands'

/**
 * One node of the formula.
 *
 * Leaves carry their own spacing (`before`/`after`, in ems) because the air
 * around `=` and around `+` is a property of the *symbol*, not of the layout: it
 * has to travel with the character through groupings, fractions and scripts, or
 * a relation inside a numerator would lose the space that makes it a relation.
 */
export type TexNode =
  | {
      kind: 'sym'
      /** One or more characters printed as a unit, in one style. */
      text: string
      italic: boolean
      before: number
      after: number
    }
  | {kind: 'text'; text: string}
  | {kind: 'space'; em: number; breakLine?: boolean}
  | {kind: 'row'; items: TexNode[]}
  | {kind: 'frac'; num: TexNode; den: TexNode}
  | {kind: 'sup'; base: TexNode; sup: TexNode}
  | {kind: 'sub'; base: TexNode; sub: TexNode}
  | {kind: 'supsub'; base: TexNode; sup: TexNode; sub: TexNode}
  | {kind: 'sqrt'; body: TexNode; index?: TexNode}
  | {kind: 'over'; body: TexNode; decoration: 'bar' | 'arrow'}
  | {kind: 'literal'; text: string}

export type ParsedFormula = {
  node: TexNode
  /**
   * What could not be typeset, in the creator's own spelling.
   *
   * Returned rather than thrown: a formula that uses one unknown command is still
   * a formula, and the reader is owed the part that *is* understood.
   */
  problems: string[]
}

type Token =
  | {t: 'open' | 'close' | 'sup' | 'sub'}
  | {t: 'cmd'; name: string}
  /** A control symbol: `\,` `\{` `\\` — a backslash and one character. */
  | {t: 'cchar'; char: string}
  | {t: 'char'; char: string}
  /**
   * A run of spaces.
   *
   * Kept as a token rather than dropped, because maths ignores whitespace and
   * `\text{...}` prints it — so the *parser* is what forgets spaces, not the
   * tokenizer, and `\text{if and only if}` is a phrase rather than `ifandonlyif`.
   */
  | {t: 'ws'; text: string}

const LETTER = /[A-Za-z]/
/** Latin letters are the ones maths sets in italic; digits and signs are not. */
const MATHS_ITALIC = /[A-Za-z]/

/** Split the source into tokens, dropping the whitespace maths does not print. */
function tokenize(source: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  while (i < source.length) {
    const char = source[i]
    if (char === '\\') {
      const next = source[i + 1]
      if (next === undefined) {
        tokens.push({t: 'char', char: '\\'})
        i += 1
        continue
      }
      if (LETTER.test(next)) {
        let j = i + 1
        while (j < source.length && LETTER.test(source[j])) j += 1
        tokens.push({t: 'cmd', name: source.slice(i + 1, j)})
        i = j
        continue
      }
      tokens.push({t: 'cchar', char: next})
      i += 2
      continue
    }
    if (char === '{') tokens.push({t: 'open'})
    else if (char === '}') tokens.push({t: 'close'})
    else if (char === '^') tokens.push({t: 'sup'})
    else if (char === '_') tokens.push({t: 'sub'})
    else if (char === '~') tokens.push({t: 'char', char: ' '})
    else if (/\s/.test(char)) {
      let j = i
      while (j < source.length && /\s/.test(source[j])) j += 1
      tokens.push({t: 'ws', text: source.slice(i, j)})
      i = j
      continue
    } else tokens.push({t: 'char', char})
    i += 1
  }
  return tokens
}

/** A run of characters of one style, with no spacing that would interrupt it. */
function symbol(text: string, spec?: {italic?: boolean; before?: number; after?: number}): TexNode {
  return {
    kind: 'sym',
    text,
    italic: spec?.italic ?? false,
    before: spec?.before ?? 0,
    after: spec?.after ?? 0,
  }
}

/**
 * The leaf a character makes.
 *
 * A letter is italic (that is what makes `abc` read as three variables rather
 * than a word), a relation or operator carries its air, and a digit, bracket or
 * unknown character is simply itself.
 */
function charLeaf(char: string): TexNode {
  const spacing = CHAR_SPACING[char]
  return symbol(char, {
    italic: MATHS_ITALIC.test(char),
    before: spacing?.before ?? 0,
    after: spacing?.after ?? 0,
  })
}

class Parser {
  private at = 0
  readonly problems: string[] = []

  constructor(private readonly tokens: Token[]) {}

  private peek(offset = 0): Token | undefined {
    return this.tokens[this.at + offset]
  }

  /** Whitespace is not part of a formula: maths skips it wherever it appears. */
  private skipWs(): void {
    while (this.tokens[this.at]?.t === 'ws') this.at += 1
  }

  /**
   * Everything up to the end of the current group.
   *
   * Scripts are folded in here rather than in `atom`, because a script attaches
   * to the *list so far* — `x_i^2` is one base with a subscript and a
   * superscript, and reading them one at a time would need the second to know
   * about the first.
   */
  list(insideGroup: boolean): TexNode {
    const items: TexNode[] = []
    while (this.at < this.tokens.length) {
      this.skipWs()
      const token = this.peek()
      if (!token) break
      if (token.t === 'close') {
        if (!insideGroup) {
          // A stray `}` is not a reason to lose the rest of the formula.
          this.problems.push('}')
          this.at += 1
          continue
        }
        break
      }
      if (token.t === 'sup' || token.t === 'sub') {
        this.at += 1
        const operand = this.atom()
        const base = items.pop() ?? {kind: 'row', items: []}
        items.push(this.attach(base, token.t, operand))
        continue
      }
      items.push(this.atom())
    }
    return coalesce({kind: 'row', items})
  }

  /** Fold a script onto its base, keeping both when the base already has one. */
  private attach(base: TexNode, which: 'sup' | 'sub', operand: TexNode): TexNode {
    if (base.kind === 'sup' && which === 'sub') {
      return {kind: 'supsub', base: base.base, sup: base.sup, sub: operand}
    }
    if (base.kind === 'sub' && which === 'sup') {
      return {kind: 'supsub', base: base.base, sup: operand, sub: base.sub}
    }
    // `x^a^b` is malformed TeX; keeping the first script and reporting the second
    // is kinder than dropping either.
    if (base.kind === 'sup' || base.kind === 'sub') {
      this.problems.push(`${which === 'sup' ? '^' : '_'} applied twice`)
      return base
    }
    return which === 'sup' ? {kind: 'sup', base, sup: operand} : {kind: 'sub', base, sub: operand}
  }

  /** One atom: a group, a command, or a character — everything but a script. */
  private atom(): TexNode {
    this.skipWs()
    const token = this.peek()
    if (!token) return {kind: 'row', items: []}
    this.at += 1

    if (token.t === 'open') {
      const group = this.list(true)
      if (this.peek()?.t !== 'close') {
        this.problems.push('missing }')
      } else {
        this.at += 1
      }
      return group
    }
    if (token.t === 'close') {
      this.problems.push('}')
      return {kind: 'row', items: []}
    }
    if (token.t === 'cmd') return this.command(token.name)
    if (token.t === 'cchar') return this.controlSymbol(token.char)
    if (token.t === 'char') return charLeaf(token.char)
    // A `^` or `_` with nothing before it: it becomes a script on nothing, which
    // is what TeX does with `^2` at the start of a formula.
    return {kind: 'row', items: []}
  }

  /**
   * A control symbol: `\,` for a space, `\{` for a brace, `\\` for a new line.
   *
   * The escapes matter more than they look: a creator writing `\{` wants a brace
   * on the page, and printing the backslash they typed instead would be the block
   * disagreeing with them about what they wrote.
   */
  private controlSymbol(char: string): TexNode {
    const space = SPACE_COMMANDS[char]
    if (space !== undefined) return {kind: 'space', em: space}
    if (char === '\\') return {kind: 'space', em: 0, breakLine: true}
    if (SYMBOL_COMMANDS[char]) return symbol(SYMBOL_COMMANDS[char], COMMAND_SPACING[char])
    return charLeaf(char)
  }

  private command(name: string): TexNode {
    if (name === 'frac' || name === 'dfrac' || name === 'tfrac') {
      const num = this.requiredArgument(name)
      const den = this.requiredArgument(name)
      return {kind: 'frac', num, den}
    }
    if (name === 'sqrt') {
      const index = this.optionalArgument()
      const body = this.requiredArgument(name)
      return index ? {kind: 'sqrt', body, index} : {kind: 'sqrt', body}
    }
    if (name === 'overline' || name === 'bar') {
      return {kind: 'over', body: this.requiredArgument(name), decoration: 'bar'}
    }
    if (name === 'vec') {
      return {kind: 'over', body: this.requiredArgument(name), decoration: 'arrow'}
    }
    if (name === 'left' || name === 'right') {
      // The delimiter itself is the next token, and its *size* is what `\left`
      // asks for. The size is not honoured: a delimiter here stays the height its
      // own glyph is, which is right for everything up to a fraction tall enough
      // to want a bigger bracket, and is better than shrinking the pair.
      const delimiter = this.peek()
      if (!delimiter) return {kind: 'row', items: []}
      if (delimiter.t === 'cchar' || delimiter.t === 'char') {
        this.at += 1
        // `\left.` is a delimiter that is not drawn.
        if (delimiter.char === '.') return {kind: 'row', items: []}
        return this.controlSymbol(delimiter.char)
      }
      return {kind: 'row', items: []}
    }
    if (TEXT_COMMANDS.has(name)) return {kind: 'text', text: this.rawArgument()}
    if (OP_NAME_COMMANDS.has(name)) return {kind: 'text', text: name}
    const space = SPACE_COMMANDS[name]
    if (space !== undefined) return {kind: 'space', em: space}
    const glyph = SYMBOL_COMMANDS[name]
    if (glyph) return symbol(glyph, COMMAND_SPACING[name])
    this.problems.push(`\\${name}`)
    return {kind: 'literal', text: `\\${name}`}
  }

  /** One required argument: a braced group, or the single atom TeX also accepts. */
  private requiredArgument(command: string): TexNode {
    const token = this.peek()
    if (!token) {
      this.problems.push(`\\${command} with nothing after it`)
      return {kind: 'row', items: []}
    }
    if (token.t === 'open') return this.atom()
    return this.atom()
  }

  /** `[n]`, for the index of an nth root. */
  private optionalArgument(): TexNode | undefined {
    this.skipWs()
    if (this.peek()?.t !== 'char' || (this.peek() as {char: string}).char !== '[') {
      return undefined
    }
    this.at += 1
    const items: TexNode[] = []
    while (this.at < this.tokens.length) {
      const token = this.peek()!
      if (token.t === 'char' && token.char === ']') {
        this.at += 1
        return coalesce({kind: 'row', items})
      }
      if (token.t === 'close' || token.t === 'open') break
      items.push(this.atom())
    }
    this.problems.push('missing ]')
    return coalesce({kind: 'row', items})
  }

  /**
   * The characters of a `\text{...}`, verbatim.
   *
   * Read from the tokens rather than from the source, so the same text is spelled
   * the same way the rest of the parser spells it — but with the spaces put back,
   * which is the one place in a formula where they are printed.
   */
  private rawArgument(): string {
    this.skipWs()
    if (this.peek()?.t !== 'open') {
      const token = this.peek()
      if (!token) return ''
      this.at += 1
      if (token.t === 'cmd') return token.name
      if (token.t === 'ws') return token.text
      return 'char' in token ? token.char : ''
    }
    this.at += 1
    let depth = 1
    let text = ''
    while (this.at < this.tokens.length && depth > 0) {
      const token = this.tokens[this.at++]
      if (token.t === 'open') depth += 1
      else if (token.t === 'close') depth -= 1
      if (depth === 0) break
      if (token.t === 'cmd') text += `\\${token.name}`
      else if (token.t === 'cchar') text += token.char
      else if (token.t === 'char') text += token.char
      else if (token.t === 'ws') text += token.text
      else if (token.t === 'sup') text += '^'
      else if (token.t === 'sub') text += '_'
      else if (token.t === 'open') text += '{'
      else text += '}'
    }
    return text
  }
}

/**
 * Merge neighbouring leaves that are printed identically.
 *
 * Two reasons, and the second is the one that matters: `abc` is three atoms to
 * TeX but one *run* to a text engine, and every leaf is a string some platform
 * has to be asked to measure (see `measure.tsx`) — so fewer leaves is fewer
 * measurements for the same page. A leaf carrying spacing is never merged, since
 * merging would have to invent a place for its air.
 */
function coalesce(node: TexNode): TexNode {
  if (node.kind !== 'row') return node
  const items: TexNode[] = []
  for (const item of node.items) {
    const previous = items[items.length - 1]
    if (
      previous?.kind === 'sym' &&
      item.kind === 'sym' &&
      previous.italic === item.italic &&
      previous.after === 0 &&
      item.before === 0
    ) {
      items[items.length - 1] = {...previous, text: previous.text + item.text}
      continue
    }
    items.push(item)
  }
  return {kind: 'row', items}
}

/** Read a formula. Never throws: what it could not read comes back in `problems`. */
export function parseFormula(source: string): ParsedFormula {
  const parser = new Parser(tokenize(source ?? ''))
  const node = parser.list(false)
  return {node, problems: parser.problems}
}
